import { CardOrderEntry } from '@/types/userSettings';

export interface OrderableCard {
  id: string;
  defaultOpen: boolean;
  hidden?: boolean;
}

/**
 * Moves hidden cards below the rest, keeping each group's order. Hidden cards
 * always sit at the bottom, so unhiding one drops it just below the last card
 * still showing.
 */
export const sortHiddenLast = <T extends OrderableCard>(cards: T[]): T[] => [
  ...cards.filter(({ hidden }) => !hidden),
  ...cards.filter(({ hidden }) => hidden)
];

/**
 * Lays a saved card order over the page's current card list.
 *
 * The saved order is reconciled on every read rather than trusted, so cards
 * added since it was saved still appear (at the end) and ones since removed
 * drop out. Nothing is mutated: the same card list is reconciled against both
 * a reader's own order and the role default they are previewing, so writing
 * `defaultOpen` back into it would leak one view's state into the other.
 */
const reconcileCardOrder = <T extends OrderableCard>(
  order: CardOrderEntry[] | undefined,
  cards: T[]
): T[] => {
  if (!Array.isArray(order)) return cards;

  const ordered = order.reduce((accumulator: T[], entry) => {
    // Orders saved by older versions can hold nulls for cards that no longer
    // exist, so entries are checked before they are read.
    const card = entry && cards.find(({ id }) => id === entry.id);
    if (card) {
      accumulator.push({
        ...card,
        defaultOpen: entry.defaultOpen,
        // The saved entry has the last word, so a card shipped hidden can be unhidden.
        hidden: entry.hidden || undefined
      });
    }

    return accumulator;
  }, []);

  const missing = cards.filter(({ id }) => !ordered.some((card) => card.id === id));

  // Cards added since the save land below the ones showing but above any
  // hidden ones, so a new card is never born hidden.
  return sortHiddenLast([...ordered, ...missing]);
};

export default reconcileCardOrder;

/**
 * The persisted shape of a card list: order plus each card's open and hidden
 * state. `hidden` is left off unless set, so orders saved before cards could
 * be hidden still compare equal to the same order saved now.
 */
export const toCardOrder = (cards: OrderableCard[]): CardOrderEntry[] =>
  cards
    .filter(Boolean)
    .map(({ defaultOpen, hidden, id }) => ({ defaultOpen, id, ...(hidden && { hidden: true }) }));
